import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { fetchPublicBranding, resolveImageUrl, submitVisitRating } from "../services/api";

// Channel-neutral "rate your visit" screen (#83). Reached from a venue-level
// QR (not tied to any order), so walk-ins and waiter-served guests can rate.
const T = {
  en: {
    how_was: "How was your visit?", food: "Food", service: "Service",
    food_comment: "Anything to share about the food? (optional)",
    service_comment: "Anything about the service? (optional)",
    submit: "Submit", sending: "Sending…", skip: "Skip",
    thank_you: "🙏 Thank you",
    feedback_received: "Your feedback was received. Thank you for helping us improve.",
    back_to_start: "Back to start", need_stars: "Please tap a star for both food and service.",
    no_venue: "We couldn't find this venue.",
  },
  am: {
    how_was: "ጉብኝትዎ እንዴት ነበር?", food: "ምግብ", service: "አገልግሎት",
    food_comment: "ስለ ምግቡ የሚሉት ካለ ይጻፉ (አማራጭ)",
    service_comment: "ስለ አገልግሎቱ የሚሉት ካለ (አማራጭ)",
    submit: "ላክ", sending: "በመላክ ላይ…", skip: "ዝለል",
    thank_you: "🙏 አመሰግናለሁ",
    feedback_received: "ግምገማዎ ደርሷል። እንድናሻሽል ስለረዱን እናመሰግናለን።",
    back_to_start: "ወደ መነሻ", need_stars: "ለምግብና ለአገልግሎት ኮከብ ይንኩ።",
    no_venue: "ይህን ድርጅት ማግኘት አልተቻለም።",
  },
  om: {
    how_was: "Daawwannaan kee akkam ture?", food: "Nyaata", service: "Tajaajila",
    food_comment: "Waa'ee nyaataa wanti jechuu barbaaddu (filannoo)",
    service_comment: "Waa'ee tajaajilaa wanti jechuu barbaaddu (filannoo)",
    submit: "Ergi", sending: "Ergamaa…", skip: "Darbi",
    thank_you: "🙏 Galatoomaa",
    feedback_received: "Yaadni kee nu gaheera. Akka fooyyessinu nu gargaarteef galatoomi.",
    back_to_start: "Gara jalqabaa", need_stars: "Nyaataa fi tajaajilaaf urjii tuqi.",
    no_venue: "Dhaabbata kana arguu hin dandeenye.",
  },
};
function pickT(lang) {
  const d = T[lang] || T.en;
  return (k) => d[k] || T.en[k] || k;
}

function StarPicker({ value, onChange, label }) {
  return (
    <fieldset style={{ border: "1px solid var(--border, #e2ddd5)", borderRadius: 12, padding: 12, margin: "12px 0" }}>
      <legend style={{ padding: "0 8px", fontWeight: 600 }}>{label}</legend>
      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)} aria-label={`${n} star${n === 1 ? "" : "s"}`}
            style={{ fontSize: 36, lineHeight: 1, border: "none", background: "transparent", cursor: "pointer",
                     color: n <= value ? "#d4af37" : "#cbd2d9", padding: "4px 8px" }}>★</button>
        ))}
      </div>
    </fieldset>
  );
}

export default function RateVisitPage() {
  const [params] = useSearchParams();
  const restaurantId = params.get("restaurant_id");
  const [branding, setBranding] = useState(null);
  const [food, setFood] = useState(0);
  const [service, setService] = useState(0);
  const [foodComment, setFoodComment] = useState("");
  const [serviceComment, setServiceComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!restaurantId) return;
    fetchPublicBranding(restaurantId).then(setBranding).catch(() => {});
  }, [restaurantId]);

  const lang = localStorage.getItem("menulink_lang") || "en";
  const t = pickT(lang);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!food || !service) { setError(t("need_stars")); return; }
    setSubmitting(true);
    try {
      await submitVisitRating({
        restaurant_id: Number(restaurantId),
        food_rating: food,
        service_rating: service,
        food_comment: foodComment.trim() || undefined,
        service_comment: serviceComment.trim() || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!restaurantId) return <div className="page center">{t("no_venue")}</div>;

  const logo = branding?.logo_url ? resolveImageUrl(branding.logo_url) : null;

  if (submitted) {
    return (
      <div className="page">
        <div className="brand-bar"><h1>{t("thank_you")}</h1></div>
        <div className="success" style={{ marginTop: 16 }}>{t("feedback_received")}</div>
        <div style={{ marginTop: 16 }}><Link to="/" className="btn ghost">{t("back_to_start")}</Link></div>
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <img src={logo || "/logo64.png"} alt="" width="40" height="40"
             style={{ borderRadius: 8, objectFit: "contain", background: "#fff" }} />
        <div style={{ flex: 1, fontWeight: 600 }}>{branding?.name || "Your visit"}</div>
      </div>
      <div className="brand-bar"><h1>{t("how_was")}</h1></div>
      <form className="card" onSubmit={submit} style={{ marginTop: 16 }}>
        <StarPicker label={t("food")} value={food} onChange={setFood} />
        <textarea className="notes" placeholder={t("food_comment")} value={foodComment}
                  onChange={(e) => setFoodComment(e.target.value)} />
        <StarPicker label={t("service")} value={service} onChange={setService} />
        <textarea className="notes" placeholder={t("service_comment")} value={serviceComment}
                  onChange={(e) => setServiceComment(e.target.value)} />
        {error && <div className="notice" style={{ marginTop: 12 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <Link to="/" className="btn secondary">{t("skip")}</Link>
          <button className="btn" type="submit" disabled={submitting}>{submitting ? t("sending") : t("submit")}</button>
        </div>
      </form>
    </div>
  );
}
